import { useEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { fetch } from '../../utils/api-wrapper';
import { getCachedInvoiceImageUrl } from '../../utils/invoice-image-cache';
import useLanguageChange from '../../utils/hooks/useLanguageChange';
import Modal from '../../components/modal';
import CommentsBadge from '../review-expense/comments-panel/comments-badge';
import LinkedTransactionsSummary from '../review-expense/linked-transactions-summary';
import InvoiceFields from './invoice-fields';
import useInvoiceTaxes from './use-invoice-taxes';
import { canReviewCardInvoice, linkedReviewInvoice, validateInvoiceReview } from './invoice-review';
import '../review-expense/index.scss';
import './index.scss';

const request = (url, method = 'GET', body = {}) => new Promise((resolve, reject) => fetch(url, method, body, response => resolve(response?.data), reject));

/** Dedicated, explicit-save review; the full transaction editor keeps its autosave behavior. */
export const CardInvoiceReview = ({ data }) => {
  const { statement_id: statementId, checksum } = useParams();
  const navigate = useNavigate();
  useLanguageChange(data.t);
  const [statement, setStatement] = useState(null);
  const [document, setDocument] = useState(null);
  const [json, setJson] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [confirmBack, setConfirmBack] = useState(false);
  const [portal, setPortal] = useState(null);
  const { calculating, taxError, retryTaxes } = useInvoiceTaxes(json, setJson);
  const statementUrl = `/c_card/statement/?statement_id=${encodeURIComponent(statementId)}`;
  const backPath = `/c_card/statement/${statementId}`;

  useEffect(() => {
    let cancelled = false;
    setStatement(null); setDocument(null); setJson(null); setPdf(null); setErrors([]); setPdfError(false);
    request(statementUrl).then(response => {
      if (cancelled) return;
      const current = response?.statement;
      const links = current?.transactions?.filter(tx => tx.checksum === checksum) || [];
      if (!links.length || links.some(tx => !canReviewCardInvoice(current, tx))) {
        setErrors(['review_invoice.errors.unavailable']);
        return;
      }
      const receipt = linkedReviewInvoice(current, links[0]);
      setStatement(current); setDocument(receipt);
      // The full editor may contain more recent corrections than the extraction.
      // Keep document-only metadata while retaining those transaction edits.
      const initial = { ...receipt.json, ...links[0].json };
      setJson({ ...initial, ListItem: (initial.ListItem || []).map((line, index) => ({ ...line, _view_id: index })) });
      getCachedInvoiceImageUrl(receipt.json?.Name || receipt.file_name || `${statementId}_${checksum}.pdf`)
        .then(url => { if (!cancelled) { setPdf(url); setPdfError(!url); } })
        .catch(() => { if (!cancelled) setPdfError(true); });
    }).catch(() => { if (!cancelled) setErrors(['review_expense.error.fetch_expense_failed']); });
    return () => { cancelled = true; };
  }, [statementUrl, statementId, checksum]);

  const save = async () => {
    if (!json || calculating || taxError || savingRef.current) return;
    const validation = validateInvoiceReview(json);
    if (validation.length) { setErrors(validation.map(key => `review_invoice.errors.${key}`)); return; }
    savingRef.current = true; setSaving(true); setErrors([]);
    try {
      // Recheck every original link before writing. Do not save a stale page to
      // a different transaction, or modify one that has since been submitted.
      const originalIds = statement.transactions.filter(tx => tx.checksum === checksum).map(tx => tx.transaction_id);
      const resolveLinks = current => {
        const links = current?.transactions?.filter(tx => tx.checksum === checksum) || [];
        if (links.length !== originalIds.length || links.some(tx => !originalIds.includes(tx.transaction_id) || !canReviewCardInvoice(current, tx))) throw new Error('unavailable');
        return links;
      };
      const fresh = (await request(statementUrl))?.statement;
      const links = resolveLinks(fresh);
      const receipt = linkedReviewInvoice(fresh, links[0]);
      await request(`/c_card/expenses/${encodeURIComponent(statementId)}/${encodeURIComponent(checksum)}`, 'PUT', {
        ...receipt, autoMatch: false, json: { ...json, Reviewed: true },
      });
      const saved = (await request(statementUrl))?.statement;
      const savedLinks = resolveLinks(saved);
      const savedJson = linkedReviewInvoice(saved, savedLinks[0]).json;
      if (!savedJson?.Reviewed) throw new Error('save_failed');
      // Preserve bank amounts and identifiers; only the reviewed document JSON
      // is synchronized. A failed write keeps the draft available for retry.
      for (const tx of savedLinks) {
        const updated = await request(`/c_card/transaction/${encodeURIComponent(statementId)}/${encodeURIComponent(tx.transaction_id)}`, 'PUT', { ...tx, json: savedJson });
        if (!updated?.json?.Reviewed) throw new Error('save_failed');
      }
      setSaving(false);
      navigate(backPath);
    } catch (error) {
      setSaving(false);
      setErrors([error?.message === 'unavailable' ? 'review_invoice.errors.unavailable' : 'review_expense.error.update_failed']);
    } finally {
      savingRef.current = false;
    }
  };

  return <div ref={setPortal} className='card-expense-review card-invoice-review'>
    {confirmBack && <Modal title={data.t.read('confirm')} cancelAction={() => setConfirmBack(false)} okAction={() => navigate(backPath)} okText={data.t.read('yes')} cancelText={data.t.read('cancel')}>
      {data.t.read('go_back_conrifmation')}
    </Modal>}
    <div className='review-page-header d-flex flex-column align-items-start mb-4'>
      <h1>{data.t.read('review_invoice.title')}</h1>
      <div className='d-flex align-items-center'>
        <button type='button' className='credit-transactions-heading__back' disabled={saving} onClick={() => setConfirmBack(true)}>
          <FontAwesomeIcon icon={faArrowLeft} aria-hidden='true' />{data.t.read('review_expense.back_to_transactions')}
        </button>
        <CommentsBadge expense={document} comments={document?.json?.comments} />
      </div>
    </div>
    <LinkedTransactionsSummary statement={statement} checksum={checksum} t={data.t} />
    {errors.length > 0 && <div role='alert' className='alert alert-danger'>{errors.map(key => <div key={key}>{data.t.read(key)}</div>)}</div>}
    {!json && !errors.length && <p role='status'>{data.t.read('loading')}</p>}
    {json && <div className='invoice-review-container'>
      <div className='invoice-review'>
        {pdf ? <object type='application/pdf' data={pdf} aria-label={data.t.read('review_invoice.document')}>
          <a href={pdf} target='_blank' rel='noopener noreferrer'>{data.t.read('download_pdf')}</a>
        </object> : <p role='status'>{data.t.read(pdfError ? 'review_expense.error.fetch_invoice_pdf_failed' : 'loading')}</p>}
      </div>
      <form className='invoice-review-form' onSubmit={event => { event.preventDefault(); save(); }} noValidate>
        <fieldset disabled={saving}>
          <InvoiceFields data={data} json={json} onChange={setJson} portal={portal} disabled={saving} />
        </fieldset>
        {calculating && <p role='status'>{data.t.read('review_invoice.calculating')}</p>}
        {taxError && <div role='alert' className='alert alert-danger'>{data.t.read('review_invoice.errors.taxes')} <button type='button' className='btn btn-link' onClick={retryTaxes}>{data.t.read('review_invoice.retry')}</button></div>}
        <div className='review-form-actions d-flex justify-content-end'>
          <button type='submit' data-testid='save-invoice-review' className='btn btn-primary' disabled={saving || calculating || taxError}>{data.t.read(saving ? 'review_invoice.saving' : 'review_expense.save_modifications')}</button>
        </div>
      </form>
    </div>}
  </div>;
};
export default inject('data')(observer(CardInvoiceReview));
